import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, Send, Smile, Meh, Frown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";

const OPTIONS = [
  { value: "great", label: "Great", icon: Smile, tone: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/40" },
  { value: "ok", label: "OK", icon: Meh, tone: "text-[hsl(var(--warning))]", border: "border-[hsl(var(--warning))]/40" },
  { value: "needs_work", label: "Needs work", icon: Frown, tone: "text-destructive", border: "border-destructive/40" },
];

/** Floating action button + modal for collecting user feedback. */
const FeedbackButton = ({ floating = true }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated) return null;

  const submit = async () => {
    if (!rating) {
      toast.error("Please pick a rating");
      return;
    }
    try {
      setSubmitting(true);
      await apiPost("/feedback", {
        rating,
        message: message || null,
        page: location.pathname + location.search,
        user_agent: navigator.userAgent,
      });
      toast.success("Thanks for the feedback!");
      setOpen(false);
      setRating(null);
      setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant={floating ? "default" : "outline"}
        size={floating ? "sm" : "sm"}
        className={
          floating
            ? "fixed bottom-20 left-4 lg:bottom-6 lg:left-6 z-40 shadow-lg gap-2"
            : "gap-2"
        }
        data-testid={floating ? "feedback-floating-btn" : "feedback-inline-btn"}
      >
        <MessageSquare className="h-4 w-4" /> {floating ? "Share feedback" : "Feedback"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How is CSE Investor working for you?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="feedback-options">
              {OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = rating === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setRating(opt.value)}
                    className={`flex flex-col items-center gap-2 rounded-lg border px-3 py-4 transition-colors ${
                      active
                        ? `${opt.border} bg-muted/40 ring-2 ring-offset-2 ring-offset-background ring-primary/30`
                        : "border-border hover:bg-muted/30"
                    }`}
                    data-testid={`feedback-rating-${opt.value}`}
                  >
                    <Icon className={`h-7 w-7 ${opt.tone}`} />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            <label className="block">
              <span className="text-xs text-muted-foreground">Tell us more (optional)</span>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What works? What’s broken? What would make it better?"
                rows={4}
                data-testid="feedback-message"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="feedback-cancel">
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting || !rating} data-testid="feedback-submit">
                <Send className="h-4 w-4" /> {submitting ? "Sending…" : "Submit feedback"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
